import Alpine from 'alpinejs';

/**
 * Carousel — Alpine data component
 *
 * Horizontal scroll-snap carousel. Wrap the whole widget in an element with
 * `x-data="carousel()"`. Inside, place the scrollable list with
 * `x-ref="scroller"`. Prev/next buttons call `prev()` / `next()`. Button
 * enable state comes from `canPrev` / `canNext`.
 *
 * Navigation moves by one slide (first child's width + column gap).
 * Keyboard arrow keys and native swipe work via the scroll container itself.
 *
 * Optional slide-shape parallax: any descendant of a slide marked with the
 * `[data-slide-parallax]` attribute gets its `--slide-parallax-x` custom
 * property updated on scroll (value = slide-centre offset from scroller
 * centre, scaled by PARALLAX_FACTOR). Slides without marked descendants
 * incur no work.
 *
 * Active-slide tracking: whenever the slide closest to the scroller's centre
 * changes, the scroller dispatches a bubbling `carousel-change` CustomEvent
 * with detail `{ index, slide, colorSlug }`. `colorSlug` comes from the
 * slide's `data-slide-color` attribute (empty → null). Consumers listen on
 * any ancestor.
 */
const PARALLAX_FACTOR = 0.2;

export const initCarousel = () => {
  Alpine.data('carousel', () => {
    let onScroll: (() => void) | null = null;
    let resizeObs: ResizeObserver | null = null;
    let scroller: HTMLElement | null = null;
    let parallaxPairs: Array<{ slide: HTMLElement; el: HTMLElement }> = [];
    let rafId: number | null = null;
    let activeIndex = -1;

    const measureStep = (): number => {
      if (!scroller) return 0;
      const first = scroller.firstElementChild as HTMLElement | null;
      if (!first) return 0;
      const gap = parseFloat(getComputedStyle(scroller).columnGap || '0') || 0;
      return first.getBoundingClientRect().width + gap;
    };

    const collectParallaxPairs = () => {
      parallaxPairs = [];
      if (!scroller) return;
      for (const child of Array.from(scroller.children)) {
        const slide = child as HTMLElement;
        const el = slide.querySelector<HTMLElement>('[data-slide-parallax]');
        if (el) parallaxPairs.push({ slide, el });
      }
    };

    const updateScrollBound = () => {
      rafId = null;
      if (!scroller) return;
      const scrollerRect = scroller.getBoundingClientRect();
      const centerX = scrollerRect.left + scrollerRect.width / 2;

      if (parallaxPairs.length > 0) {
        for (const { slide, el } of parallaxPairs) {
          const slideRect = slide.getBoundingClientRect();
          const slideCenter = slideRect.left + slideRect.width / 2;
          const offset = (slideCenter - centerX) * PARALLAX_FACTOR;
          el.style.setProperty('--slide-parallax-x', `${offset}px`);
        }
      }

      const slides = Array.from(scroller.children) as HTMLElement[];
      if (slides.length === 0) return;
      let closestIdx = 0;
      let closestDist = Infinity;
      for (let i = 0; i < slides.length; i++) {
        const r = slides[i].getBoundingClientRect();
        const c = r.left + r.width / 2;
        const d = Math.abs(c - centerX);
        if (d < closestDist) {
          closestDist = d;
          closestIdx = i;
        }
      }
      if (closestIdx !== activeIndex) {
        activeIndex = closestIdx;
        const slide = slides[closestIdx];
        const colorSlug = slide.dataset.slideColor || null;
        scroller.dispatchEvent(new CustomEvent('carousel-change', {
          bubbles: true,
          detail: { index: closestIdx, slide, colorSlug },
        }));
      }
    };

    const scheduleUpdate = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(updateScrollBound);
    };

    return {
      canPrev: false as boolean,
      canNext: true as boolean,

      init() {
        scroller = (this.$refs['scroller'] as HTMLElement) ?? null;
        if (!scroller) return;

        collectParallaxPairs();

        onScroll = () => {
          this.updateEdges();
          scheduleUpdate();
        };
        scroller.addEventListener('scroll', onScroll, { passive: true });

        resizeObs = new ResizeObserver(() => {
          this.updateEdges();
          collectParallaxPairs();
          scheduleUpdate();
        });
        resizeObs.observe(scroller);

        this.$nextTick(() => {
          this.updateEdges();
          updateScrollBound();
        });
      },

      destroy() {
        if (scroller && onScroll) scroller.removeEventListener('scroll', onScroll);
        resizeObs?.disconnect();
        if (rafId !== null) cancelAnimationFrame(rafId);
        resizeObs = null;
        onScroll = null;
        scroller = null;
        parallaxPairs = [];
        rafId = null;
        activeIndex = -1;
      },

      updateEdges() {
        if (!scroller) return;
        const { scrollLeft, scrollWidth, clientWidth } = scroller;
        this.canPrev = scrollLeft > 1;
        this.canNext = scrollLeft < scrollWidth - clientWidth - 1;
      },

      next() {
        scroller?.scrollBy({ left: measureStep(), behavior: 'smooth' });
      },

      prev() {
        scroller?.scrollBy({ left: -measureStep(), behavior: 'smooth' });
      },
    };
  });
};
